import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { BRANCHES } from "@/lib/branches";
import { PageHeader, TableShell } from "@/components/EntityManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Satellite, Radio, ExternalLink, Search, Download, Upload, Printer, FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { printVehicleTrackingRegister } from "@/lib/print";
import { toast } from "sonner";

const TrackButton = ({ id, link, kind }) => {
  if (!id && !link) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col items-start gap-1">
      {id && <span className="text-sm tabular-nums">{id}</span>}
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`${kind}-track-btn`}>
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
            {kind === "fastag" ? <Radio className="h-3.5 w-3.5" /> : kind === "eway" ? <FileText className="h-3.5 w-3.5" /> : <Satellite className="h-3.5 w-3.5" />}
            {kind === "eway" ? "EWB Portal" : "Track"} <ExternalLink className="h-3 w-3" />
          </Button>
        </a>
      ) : (
        <span className="text-xs text-muted-foreground">No link</span>
      )}
    </div>
  );
};

export default function VehiclesTracking() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [importing, setImporting] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [branchFilter, setBranchFilter] = useState("All");

  const reload = async () => {
    try {
      const res = await api.get("/vehicles");
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const split = (l) => {
      const out = [];
      let cur = "", inQ = false;
      for (let i = 0; i < l.length; i++) {
        const c = l[i];
        if (c === '"') {
          if (inQ && l[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ;
        } else if (c === "," && !inQ) { out.push(cur); cur = ""; } else cur += c;
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const header = split(lines[0]).map((h) => h.toLowerCase().replace(/[^a-z]/g, ""));
    const idx = (names) => header.findIndex((h) => names.some((n) => h.includes(n)));
    const iReg = idx(["registration", "regno", "vehicle"]);
    const iFn = idx(["fastagno", "fastagnumber"]);
    const iFl = idx(["fastaglink", "fastagurl"]);
    const iGid = idx(["gpsid", "gpsdevice", "imei"]);
    const iGl = idx(["gpslink", "gpsurl"]);
    const iGp = idx(["gpsprovider", "provider"]);
    const iEn = idx(["ewaybillno", "ewayno", "ewbno", "ewaybill"]);
    const iEl = idx(["ewaylink", "ewayurl", "ewblink"]);
    return lines.slice(1).map((l) => {
      const c = split(l);
      return {
        registration_no: iReg >= 0 ? c[iReg] : "",
        fastag_no: iFn >= 0 ? c[iFn] : undefined,
        fastag_link: iFl >= 0 ? c[iFl] : undefined,
        gps_id: iGid >= 0 ? c[iGid] : undefined,
        gps_link: iGl >= 0 ? c[iGl] : undefined,
        gps_provider: iGp >= 0 ? c[iGp] : undefined,
        eway_bill_no: iEn >= 0 ? c[iEn] : undefined,
        eway_link: iEl >= 0 ? c[iEl] : undefined,
      };
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      const norm = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
      const current = rows || [];
      let updated = 0, skipped = 0;
      for (const p of parsed) {
        const veh = current.find((v) => norm(v.registration_no) === norm(p.registration_no));
        if (!veh || !norm(p.registration_no)) { skipped++; continue; }
        const payload = {
          ...veh,
          ...(p.fastag_no !== undefined ? { fastag_no: p.fastag_no } : {}),
          ...(p.fastag_link !== undefined ? { fastag_link: p.fastag_link } : {}),
          ...(p.gps_id !== undefined ? { gps_id: p.gps_id } : {}),
          ...(p.gps_link !== undefined ? { gps_link: p.gps_link } : {}),
          ...(p.gps_provider !== undefined ? { gps_provider: p.gps_provider } : {}),
          ...(p.eway_bill_no !== undefined ? { eway_bill_no: p.eway_bill_no } : {}),
          ...(p.eway_link !== undefined ? { eway_link: p.eway_link } : {}),
        };
        await api.put(`/vehicles/${veh.id}`, payload);
        updated++;
      }
      await reload();
      toast.success(`Import done: ${updated} vehicle(s) updated${skipped ? `, ${skipped} skipped (no matching registration)` : ""}`);
    } catch (err) {
      toast.error("Could not read CSV. Use columns: Registration No., FASTag No., FASTag Link, GPS Provider, GPS ID, GPS Link, E-Way Bill No., E-Way Bill Link");
    } finally {
      setImporting(false);
    }
  };

  const ewbState = (r) => {
    if (!r.eway_valid_upto) return null;
    const days = Math.round((new Date(r.eway_valid_upto) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
    return { days, expired: days < 0, soon: days >= 0 && days <= 1 };
  };
  const ewbAlerts = (rows || []).filter((r) => {
    const st = ewbState(r);
    return st && (st.expired || st.soon);
  });

  const filtered = (rows || []).filter((r) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [r.registration_no, r.vehicle_no, r.branch, r.model, r.fastag_no, r.gps_id, r.gps_provider, r.eway_bill_no].some((v) => String(v || "").toLowerCase().includes(t));
  }).filter((r) => branchFilter === "All" || (r.branch || "") === branchFilter);
  const selectedRows = filtered.filter((r) => sel.has(r.id));
  const allSel = filtered.length > 0 && filtered.every((r) => sel.has(r.id));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(filtered.map((r) => r.id)));
  const toggleOne = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSubmit = () => {
    const norm = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
    const key = norm(q);
    if (!key) {
      toast.error("Type a vehicle registration number first");
      return;
    }
    const veh = (rows || []).find((v) => norm(v.registration_no) === key)
      || (rows || []).find((v) => norm(v.registration_no).includes(key));
    if (!veh) {
      toast.error(`No vehicle found matching "${q}". Add it from Company Vehicles.`);
      return;
    }
    if (veh.gps_link) {
      window.open(veh.gps_link, "_blank", "noopener");
      toast.success(`Opening GPS tracking for ${veh.registration_no}`);
    } else if (veh.fastag_link) {
      window.open(veh.fastag_link, "_blank", "noopener");
      toast.success(`Opening FASTag tracking for ${veh.registration_no}`);
    } else {
      toast.error(`${veh.registration_no} has no tracking link yet. Add one from Company Vehicles.`);
    }
  };

  const exportCsv = () => {
    const header = "Sl No.,Branch,Registration No.,Vehicle No.,Vehicle Type,Model,Model Type,FASTag No.,FASTag Link,GPS Provider,GPS ID,GPS Link,E-Way Bill No.,E-Way Bill Link";
    const csv = [
      header,
      ...filtered.map((r, i) =>
        [i + 1, r.branch, r.registration_no, r.vehicle_no, r.type, r.model, r.model_type, r.fastag_no, r.fastag_link, r.gps_provider, r.gps_id, r.gps_link, r.eway_bill_no, r.eway_link]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "vehicles-tracking.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Vehicles Tracking"
        subtitle="Track your fleet via FASTag (Fast Tracking) and GPS Tracking links."
        action={
          <div className="flex items-center gap-2">
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="w-[140px]" data-testid="tracking-branch-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["All", ...BRANCHES].map((b) => (
                  <SelectItem key={b} value={b}>{b === "All" ? "All Branches" : b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search vehicle / FASTag / GPS"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                data-testid="tracking-search"
              />
            </div>
            <Button onClick={handleSubmit} data-testid="tracking-submit-btn">
              <Satellite className="mr-2 h-4 w-4" /> Submit
            </Button>
            <Button variant="outline" onClick={() => printVehicleTrackingRegister(selectedRows.length ? selectedRows : filtered)} disabled={!filtered.length} data-testid="tracking-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{sel.size ? ` (${selectedRows.length})` : ""}
            </Button>
            <Button variant="outline" onClick={exportCsv} data-testid="tracking-export-btn">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <label>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImport} data-testid="tracking-import-input" />
              <Button asChild variant="outline" disabled={importing}>
                <span className="cursor-pointer" data-testid="tracking-import-btn">
                  <Upload className="mr-2 h-4 w-4" /> {importing ? "Importing…" : "Import CSV"}
                </span>
              </Button>
            </label>
          </div>
        }
      />

      {ewbAlerts.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50/70 px-4 py-3" data-testid="ewb-expiry-alert">
          <div className="flex items-center gap-2 text-sm font-bold text-red-800">
            <FileText className="h-4 w-4" /> E-Way Bill Expiry Alert — {ewbAlerts.length} vehicle(s) need a fresh/extended EWB
          </div>
          <div className="mt-1 space-y-0.5">
            {ewbAlerts.slice(0, 5).map((r) => {
              const st = ewbState(r);
              return (
                <div key={r.id} className="text-xs text-red-700" data-testid="ewb-expiry-row">
                  <b>{r.registration_no}</b> — EWB {r.eway_bill_no || "—"} {st.expired ? `expired ${Math.abs(st.days)} day(s) ago` : st.days === 0 ? "expires TODAY" : "expires tomorrow"} (valid upto {r.eway_valid_upto})
                </div>
              );
            })}
          </div>
        </div>
      )}
      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-[36px]">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} disabled={!filtered.length} data-testid="tracking-select-all" />
              </TableHead>
              {["SL No.", "Branch", "Registration No.", "Vehicle No.", "Vehicle Type", "Model", "Model Type", "Fast Tracking (FASTag)", "GPS Provider", "GPS Tracking", "E-Way Bill"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3, 4].map((i) => (
                <TableRow key={i}>
                  {[1, 2, 3, 4, 5].map((c) => (
                    <TableCell key={c}>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                  No vehicles found. Add FASTag / GPS tracking links from the Company Vehicles page.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r, i) => (
              <TableRow key={r.id} data-testid="tracking-row">
                <TableCell>
                  <Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid="tracking-select-row" />
                </TableCell>
                <TableCell className="text-sm">{i + 1}</TableCell>
                <TableCell className="text-sm">{r.branch || "—"}</TableCell>
                <TableCell className="text-sm font-medium">{r.registration_no || "—"}</TableCell>
                <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                <TableCell className="text-sm">{r.type || "—"}</TableCell>
                <TableCell className="text-sm">{r.model || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="tracking-model-type-cell">{r.model_type || "—"}</TableCell>
                <TableCell><TrackButton id={r.fastag_no} link={r.fastag_link} kind="fastag" /></TableCell>
                <TableCell className="text-sm" data-testid="gps-provider-cell">{r.gps_provider || "—"}</TableCell>
                <TableCell><TrackButton id={r.gps_id} link={r.gps_link} kind="gps" /></TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <TrackButton id={r.eway_bill_no} link={r.eway_bill_no || r.eway_link ? r.eway_link || "https://ewaybillgst.gov.in/Others/EBPrintnew.aspx" : ""} kind="eway" />
                    {(() => {
                      const st = ewbState(r);
                      if (!st) return null;
                      return (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.expired ? "bg-red-100 text-red-700" : st.soon ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                          data-testid="ewb-expiry-chip"
                        >
                          {st.expired ? `EWB EXPIRED ${Math.abs(st.days)}d ago` : st.days === 0 ? "EWB expires TODAY" : st.days === 1 ? "EWB expires tomorrow" : `EWB valid ${st.days}d`}
                        </span>
                      );
                    })()}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>
    </div>
  );
}
